import { useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { loginSchema } from "@codestack/shared";
import { Alert, Button, Input } from "@codestack/ui";
import { useAuth } from "../lib/auth";
import { ApiError } from "../lib/api";

export function LoginPage() {
  const { login, status } = useAuth();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (status === "authenticated") {
    const from = (location.state as { from?: string } | null)?.from ?? "/";
    return <Navigate to={from} replace />;
  }

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setServerError(null);

    const parsed = loginSchema.safeParse({ email, password });
    if (!parsed.success) {
      const fieldErrors: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0];
        if (typeof key === "string" && !fieldErrors[key]) {
          fieldErrors[key] = issue.message;
        }
      }
      setErrors(fieldErrors);
      return;
    }

    setErrors({});
    setSubmitting(true);

    try {
      await login(parsed.data.email, parsed.data.password);
    } catch (cause) {
      setServerError(
        cause instanceof ApiError
          ? cause.status === 401
            ? "Those credentials were not accepted."
            : cause.message
          : "Unable to sign in right now.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden px-4 py-12">
      <div
        className="pointer-events-none absolute inset-0 bg-grid-fade opacity-60"
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute left-1/2 top-0 h-[420px] w-[720px] -translate-x-1/2 rounded-full bg-brand-500/15 blur-[130px]"
        aria-hidden="true"
      />

      <div className="relative w-full max-w-sm">
        <div className="mb-8 flex flex-col items-center text-center">
          {/* Light-ink lockup for the dark sign-in screen. */}
          <img
            src="/brand/logo-light.png"
            alt="CodeStack Technologies"
            width={116}
            height={40}
            style={{ height: 34, width: "auto" }}
            className="block select-none"
            draggable={false}
          />
          <h1 className="mt-5 font-display text-xl font-bold text-white">
            CodeStack Admin
          </h1>
          <p className="mt-1 text-sm text-ink-400">
            Sign in to manage platforms and payments.
          </p>
        </div>

        <form
          onSubmit={submit}
          noValidate
          className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-6 backdrop-blur-sm"
        >
          {serverError ? (
            <div className="mb-5">
              <Alert tone="danger">{serverError}</Alert>
            </div>
          ) : null}

          <div className="space-y-4">
            <Input
              label="Email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              error={errors.email}
              autoComplete="username"
              autoFocus
              placeholder="you@codestacktechnologies.com"
            />
            <Input
              label="Password"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              error={errors.password}
              autoComplete="current-password"
              placeholder="••••••••••"
            />
          </div>

          <Button
            type="submit"
            className="mt-6 w-full"
            loading={submitting}
            disabled={submitting}
          >
            Sign in
          </Button>

          <p className="mt-5 text-center text-[11px] leading-relaxed text-ink-500">
            Access is restricted to authorised CodeStack staff. All sign-in attempts
            are logged.
          </p>
        </form>
      </div>
    </div>
  );
}
